import { ReactNode, useRef, createContext, useContext } from 'react';
import { useNavContext } from '@/hooks/use-nav-context';
import { UnifiedMobileNav } from './unified-mobile-nav';
import { FloatingActionProvider, FloatingActionButton } from './floating-action-slot';
import { ConsentBanner } from '@/components/legal/consent-banner';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

// Context for chat widget integration
interface ChatContextType {
  registerChatHandler: (handler: () => void) => void;
  unregisterChatHandler: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function useChatHandler() {
  const context = useContext(ChatContext);
  return context;
}

interface MobileAppShellProps {
  children: ReactNode;
}

export function MobileAppShell({ children }: MobileAppShellProps) {
  const navContext = useNavContext();
  const chatHandlerRef = useRef<(() => void) | null>(null);

  // Fetch tournament for primary color if in tournament context
  const { data: tournament } = useQuery<{ primaryColor?: string | null }>({
    queryKey: ['/api/tournaments', navContext.tournamentId],
    enabled: navContext.context === 'tournament-public' && !!navContext.tournamentId,
  });

  const registerChatHandler = (handler: () => void) => {
    chatHandlerRef.current = handler;
  };

  const unregisterChatHandler = () => {
    chatHandlerRef.current = null;
  };

  const handleChatOpen = () => {
    if (chatHandlerRef.current) {
      chatHandlerRef.current();
    }
  };

  const hideChrome = navContext.context === 'none';

  return (
    <FloatingActionProvider>
      <ChatContext.Provider value={{ registerChatHandler, unregisterChatHandler }}>
        {/* ESPN PRO: Global shell with brushed steel atmosphere */}
        <div
          className={cn(
            'min-h-dvh min-h-[100dvh] flex flex-col relative bg-background text-foreground',
            !hideChrome && 'pb-32 pb-safe md-tall:pb-8'
          )}
        >
          <ImpersonationBanner />
          <div className={cn('flex-1', !hideChrome && 'pb-20')}>{children}</div>

          {/* Unified Mobile Navigation */}
          {!hideChrome && (
            <UnifiedMobileNav
              context={navContext.context}
              tournamentId={navContext.tournamentId}
              organizationId={navContext.organizationId}
              coachTeamToken={navContext.coachTeamToken}
              isAuthenticated={navContext.isAuthenticated}
              primaryColor={tournament?.primaryColor || undefined}
              onChatOpen={navContext.context === 'tournament-public' ? handleChatOpen : undefined}
            />
          )}

          {/* Floating Action Button */}
          <FloatingActionButton />

          {/* Consent Banner - shows when user needs to re-consent */}
          <ConsentBanner />
        </div>
      </ChatContext.Provider>
    </FloatingActionProvider>
  );
}
